import React, { useMemo, useState } from "react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

function calcHollidaySegar(weightKg) {
  if (!weightKg || weightKg <= 0) return null;
  const w = Number(weightKg);

  let perDay = 0;
  let perHour = 0;
  const breakdown = [];

  const first = Math.min(w, 10);
  perDay += first * 100;
  perHour += first * 4;
  breakdown.push(`${first} kg × 100 ml/kg/day = ${first * 100} ml/day  (× 4 ml/kg/hr = ${first * 4} ml/hr)`);

  if (w > 10) {
    const second = Math.min(w - 10, 10);
    perDay += second * 50;
    perHour += second * 2;
    breakdown.push(`${second} kg × 50 ml/kg/day  = ${second * 50} ml/day  (× 2 ml/kg/hr = ${second * 2} ml/hr)`);
  }
  if (w > 20) {
    const third = w - 20;
    perDay += third * 20;
    perHour += third * 1;
    breakdown.push(`${third} kg × 20 ml/kg/day  = ${third * 20} ml/day  (× 1 ml/kg/hr = ${third} ml/hr)`);
  }

  const macroGtt = (perHour * 20) / 60; // 20 gtt/ml
  const microMgtt = perHour; // 60 mgtt/ml → equals ml/hr numerically

  return { perHour, perDay, macroGtt, microMgtt, breakdown: breakdown.join("\n") };
}

export default function IVFluid() {
  const t = useT();
  const [weight, setWeight] = useState("");
  const result = useMemo(() => calcHollidaySegar(parseFloat(weight)), [weight]);

  return (
    <div data-testid="page-iv-fluid">
      <PageHeader title={t("iv.title")} subtitle={t("iv.subtitle")} formula={t("iv.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          <Field label={t("iv.weight")} unit="kg" testid="weight">
            <NumberInput
              value={weight}
              onChange={setWeight}
              placeholder={t("iv.weight.placeholder")}
              min={0}
              testid="weight-input"
            />
          </Field>

          <Note kind="info">{t("iv.note")}</Note>
        </div>

        <div className="space-y-4">
          <Result
            label={t("iv.result.rate")}
            value={result ? fmt(result.perHour, 1) : "—"}
            unit="ml / hr"
            big
            testid="result-per-hour"
          />
          <Result
            label={t("iv.result.day")}
            value={result ? fmt(result.perDay, 0) : "—"}
            unit="ml / 24 h"
            testid="result-per-day"
          />
          <Result
            label={t("iv.macro")}
            value={result ? fmt(result.macroGtt, 0) : "—"}
            unit="gtt / min"
            testid="result-macro"
          />
          <Result
            label={t("iv.micro")}
            value={result ? fmt(result.microMgtt, 0) : "—"}
            unit="mgtt / min"
            testid="result-micro"
          />
          <FormulaBox testid="breakdown-box">
            {result ? result.breakdown : t("iv.breakdown.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
